import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Chip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyListSmsIntegrationsQuery } from "store/services/smsIntegrationApi";

import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useTablePagination } from "hooks";

const getStatusStyle = (status) => {
  const styles = {
    Active: { backgroundColor: "#EBF5FB", textColor: "green" },
    Inactive: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  };

  return (
    styles[status] || {
      backgroundColor: "#F8F9F9",
      textColor: "#7F8C8D",
    }
  );
};

const SmsIntegrationListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    { title: "Settings", path: "/app/crm/setting/list" },
    { title: "SMS Integrations" },
  ];
  const smsIntegrationTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
       {
      name: "Entity",
      selector: (row) => row.entity?.name,
      cell: (row) => row.entity?.name || "N/A",
    },
    {
      name: "Provider",
      selector: (row) => row.provider?.name,
      cell: (row) => (
        <Link
          title={row?.provider?.name}
          to={`/app/crm/sms-integrations/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.provider?.name ? (
            <HighlightedText text={row?.provider?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
       {
      name: "Vendor",
      selector: (row) => row.vendor?.shopName,
      cell: (row) => row.vendor?.shopName || "N/A",
    },
    {
      name: "Default",
      selector: (row) => row.isDefault,
      cell: (row) => {
        const statusName = row.isDefault ? "Yes" : "No";
        const { backgroundColor, textColor } = getStatusStyle(statusName === "Yes" ? "Active" : "Inactive");

        return (
          <Chip
            label={statusName}
            size="small"
            sx={{
              backgroundColor,
              color: textColor,
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          />
        );
      },
    },
     {
      name: "Created At",
      selector: (row) => row.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/sms-integrations/${row._id}/detail`}>
            <IconButton size="small" aria-label="View SMS Integration" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/sms-integrations/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit SMS Integration">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchSmsIntegrations,
    { data: smsIntegrationRes, isLoading, isFetching, error },
  ] = useLazyListSmsIntegrationsQuery();

  React.useEffect(() => {
    fetchSmsIntegrations({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchSmsIntegrations, keyword, perPage]);

  useAppMeta(`${activeTab} SMS Integrations`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={smsIntegrationTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Search SMS Integrations"
          disabled={isLoading || isFetching}
        ></TableFilter>

        <AppTable
          columns={columns}
          data={smsIntegrationRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={smsIntegrationRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default SmsIntegrationListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/sms-integrations/create">
      <Button startIcon={<Add />} variant="contained">
        New SMS Integration
      </Button>
    </Link>
  );
};